import type { ResultRow } from './InviteResultsPanel'

import { BulkInviteResponse } from '@/api/generated'
import { Role } from '@/types'
import { isValidChipEmail, normalizeEmailInput } from '@/utils/email'

export const makeInviteValidator = (currentUserEmail: string, memberEmails: string[], invitedEmails: string[]) => {
  const normalizedSelf = currentUserEmail.trim().toLowerCase()
  const members = new Set(memberEmails.map((e) => e.toLowerCase()))
  const invited = new Set(invitedEmails.map((e) => e.toLowerCase()))
  return (email: string): string | null => {
    const normalized = email.trim().toLowerCase()
    // Checked before the roster, so it fires even while members/invitations are still
    // loading — currentUserEmail is empty pre-load and never matches, so this fails open.
    if (normalizedSelf && normalized === normalizedSelf) return 'You cannot invite yourself'
    if (members.has(normalized)) return 'User already in team'
    if (invited.has(normalized)) return 'User already invited'
    return null
  }
}

export const collectEmailsForSubmit = (emails: string[], draft: string): { emails: string[] } | { error: string } => {
  const draftEmail = normalizeEmailInput(draft)
  if (draftEmail) {
    if (!isValidChipEmail(draftEmail)) return { error: 'Please enter a valid email' }
    return { emails: emails.includes(draftEmail) ? emails : [...emails, draftEmail] }
  }
  if (emails.length === 0) return { error: 'Please enter a valid email' }
  return { emails }
}

// Backend's resend/upsert path treats an omitted `team_id` key as "preserve
// existing team_id" and an explicit `null` differently — so an unset team must
// drop the key entirely, never send `team_id: null`.
export const attachTeamId = <T extends { email: string; role?: Role }>(
  invites: T[],
  teamId?: string | null
): Array<T & { team_id?: string }> => (teamId ? invites.map((invite) => ({ ...invite, team_id: teamId })) : invites)

export interface BulkInviteCategories {
  invited: string[]
  self: string[]
  alreadyMember: string[]
  alreadyInOtherTeam: string[]
  alreadyInvited: string[]
  failed: Array<{ email: string; reason: string }>
  retryable: string[]
}

// These keys, 'cannot_invite_self', and the 'already in an enterprise' substring
// below are contract strings emitted per-email by bulk_invite_users in
// aria-web/src/backend_service/services/enterprise/api_service.py — not display
// copy. If the backend rewords one, that email degrades to the generic
// non-retryable "Couldn't be invited" bucket.
const RETRYABLE_ERRORS: Record<string, string> = {
  failed_to_send_email: "Couldn't send the invite email",
  internal_error: 'Something went wrong',
}

export const categorizeBulkResult = (result: BulkInviteResponse, memberEmails: string[]): BulkInviteCategories => {
  const members = new Set(memberEmails.map((e) => e.toLowerCase()))
  const categories: BulkInviteCategories = {
    invited: [],
    self: [],
    alreadyMember: [],
    alreadyInOtherTeam: [],
    alreadyInvited: [],
    failed: [],
    retryable: [],
  }
  for (const invite of result.successful) {
    categories.invited.push(invite.email)
  }
  for (const { email, error } of result.failed) {
    if (error === 'cannot_invite_self') {
      categories.self.push(email)
    } else if (error === 'invitation_exists') {
      categories.alreadyInvited.push(email)
    } else if (error.includes('already in an enterprise')) {
      if (members.has(email.toLowerCase())) {
        categories.alreadyMember.push(email)
      } else {
        categories.alreadyInOtherTeam.push(email)
      }
    } else {
      categories.failed.push({ email, reason: RETRYABLE_ERRORS[error] ?? error })
      if (error in RETRYABLE_ERRORS) {
        categories.retryable.push(email)
      }
    }
  }
  return categories
}

export const bulkInviteResultRows = (categories: BulkInviteCategories): ResultRow[] => {
  const rows: ResultRow[] = []
  if (categories.invited.length > 0) {
    rows.push({ label: `Invited (${categories.invited.length})`, kind: 'success' })
  }
  if (categories.self.length > 0) {
    rows.push({
      label: `Skipped your own email (${categories.self.length})`,
      detail: categories.self.join(', '),
      kind: 'info',
    })
  }
  if (categories.alreadyMember.length > 0) {
    rows.push({
      label: `Already on your team (${categories.alreadyMember.length})`,
      detail: categories.alreadyMember.join(', '),
      kind: 'info',
    })
  }
  if (categories.alreadyInOtherTeam.length > 0) {
    rows.push({
      label: `Already belongs to another team (${categories.alreadyInOtherTeam.length})`,
      detail: categories.alreadyInOtherTeam.join(', '),
      kind: 'error',
    })
  }
  if (categories.alreadyInvited.length > 0) {
    rows.push({
      label: `Already invited (${categories.alreadyInvited.length})`,
      detail: categories.alreadyInvited.join(', '),
      kind: 'info',
    })
  }
  if (categories.failed.length > 0) {
    rows.push({
      label: `Couldn't be invited (${categories.failed.length})`,
      detail: categories.failed.map(({ email, reason }) => `${email} (${reason})`).join(', '),
      kind: 'error',
    })
  }
  return rows
}
